import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import { type LWApiPublicOrderListResultRaw } from '@liquidweb-dev/club-sauce/public/order/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import type {
	LWApiPublicMarketCartDetailsResultRawI,
	MarketCartProductDetailsI,
} from '@liquidweb-dev/club-sauce/public/market/cart/index.raw';
import api from 'modules/queries/api';
import { MARKET_CART_DETAILS_QUERY_KEY } from 'modules/queries/market/cart/useDetails';
import useOrderList from 'modules/queries/order/useList';
import { ADD_ON_PRODUCT_CODES } from './addOnProductCodes';

/**
 * The order list and the carts of new orders change rarely, and ordering
 * invalidates them, so they stay fresh for a long time.
 */
const STALE_TIME = 10 * 60 * 1000;

export interface NewOrderedAddOn {
	code: string;
	related_subaccnt: string;
}

type CartItem = NonNullable<
	LWApiPublicMarketCartDetailsResultRawI['items']
>[number];

const getItemProducts = (item: CartItem): MarketCartProductDetailsI[] => {
	switch (item.type) {
		case 'product':
			return [item.details];
		case 'package':
			return item.details.products;
		case 'bundle':
			return item.details.packages.flatMap((pkg) => pkg.products);
		default:
			return [];
	}
};

const getCartAddOns = (
	cart: LWApiPublicMarketCartDetailsResultRawI,
): NewOrderedAddOn[] =>
	(cart.items || [])
		.flatMap(getItemProducts)
		.flatMap(({ code, properties }) => {
			const relatedSubaccnt = properties?.related_subaccnt;
			return ADD_ON_PRODUCT_CODES.includes(code) &&
				typeof relatedSubaccnt === 'string'
				? [{ code, related_subaccnt: relatedSubaccnt }]
				: [];
		});

/**
 * Lists the add-ons in the account's new orders, each with the subaccount it
 * belongs to. It combines the order list and cart details caches and keeps no
 * cache of its own. `isLoading` stays true until the order list and the cart of
 * every new order have loaded.
 */
const useNewOrderedAddOns = (
	options?: UseQueryOptions<LWApiPublicOrderListResultRaw, ApiErrorI>,
): { newOrderedAddOns: NewOrderedAddOn[]; isLoading: boolean } => {
	const { data: orderList, isInitialLoading: isLoadingOrderList } =
		useOrderList({ page_size: '9999' }, { staleTime: STALE_TIME, ...options });

	const newOrders = (orderList?.items || []).filter(
		({ status }) => status.toLowerCase() === 'new',
	);

	const cartQueries = useQueries({
		queries: newOrders.map(({ market_cart_uuid: uuid }) => ({
			queryKey: [MARKET_CART_DETAILS_QUERY_KEY, { uuid }],
			queryFn: () => api.market.cart.apiRaw.details({ uuid }),
			staleTime: STALE_TIME,
		})),
	});

	return {
		newOrderedAddOns: cartQueries.flatMap(({ data }) =>
			data ? getCartAddOns(data) : [],
		),
		isLoading:
			isLoadingOrderList ||
			cartQueries.some(({ isInitialLoading }) => isInitialLoading),
	};
};

export default useNewOrderedAddOns;
